import { Button, Divider, Switch, Tooltip } from '@mantine/core';
import { variants } from '@uag/react-core';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { FRUSTUM_VIEWER_RELATIVE_PATH } from 'shared/constants';

import type {
    AoQuality,
    EdgeLineThickness,
    MaterialQuality,
    Multisampling
} from 'app/modules/wallforming/components/setting-container/types/graphicSettings';

const IS_DEV_TOOLS = import.meta.env.VITE_DEV_TOOLS === 'true';
const IS_DEV_ENV =
    import.meta.env.VITE_ENVIRONMENT === 'Local Environment' || import.meta.env.VITE_ENVIRONMENT === 'Dev Environment';

export const GraphicsSettings = () => {
    const { t } = useTranslation();

    const {
        graphicsSettings,
        setGraphicsSettings,
        prefabLayerTreeOpen,
        setPrefabLayerTreeOpen,
        cameraFrustumViewerRef,
        setCameraFrustumViewerRef
    } = usePlacementStore(
        useShallow((state) => ({
            graphicsSettings: state.graphicsSettings,
            setGraphicsSettings: state.setGraphicsSettings,
            prefabLayerTreeOpen: state.prefabLayerTreeOpen,
            setPrefabLayerTreeOpen: state.setPrefabLayerTreeOpen,
            cameraFrustumViewerRef: state.cameraFrustumViewerRef,
            setCameraFrustumViewerRef: state.setCameraFrustumViewerRef
        }))
    );

    // Track open state for the frustum viewer tab
    const [frustumViewerOpen, setFrustumViewerOpen] = useState<boolean>(false);

    const handleFrustumViewerToggle = useCallback(
        (checked: boolean) => {
            if (checked) {
                // Open new window with equal search params
                const newWindow = window.open(FRUSTUM_VIEWER_RELATIVE_PATH + window.location.search, '_blank');
                setCameraFrustumViewerRef(newWindow);
                setFrustumViewerOpen(true);
                if (newWindow) {
                    // reset ref and open state when the user manually closes the window
                    const windowClosing = setInterval(() => {
                        if (newWindow.closed) {
                            clearInterval(windowClosing);
                            setCameraFrustumViewerRef(null);
                            setFrustumViewerOpen(false);
                        }
                    }, 300);
                }
            } else {
                cameraFrustumViewerRef.current?.close();
                setCameraFrustumViewerRef(null);
                setFrustumViewerOpen(false);
            }
        },
        [cameraFrustumViewerRef, setCameraFrustumViewerRef]
    );

    const aoIsActive = graphicsSettings.aoQuality !== 'off';

    const aoOptions: { label: string; value: AoQuality }[] = [
        { label: t('labelAoOff'), value: 'off' },
        { label: t('labelAoLow'), value: 'performance' },
        { label: t('labelAoMedium'), value: 'high' },
        { label: t('labelAoHigh'), value: 'ultra' }
    ];

    const materialOptions: { label: string; value: MaterialQuality }[] = [
        { label: t('labelMaterialBasic'), value: 'basic' },
        { label: t('labelMaterialStandard'), value: 'standard' },
        { label: t('labelMaterialPhysical'), value: 'physical' }
    ];

    const multisamplingOptions: { label: string; value: Multisampling }[] = [
        { label: t('labelMsaaOff'), value: 0 },
        { label: '2x', value: 2 },
        { label: '4x', value: 4 },
        { label: '8x', value: 8 }
    ];

    const edgeThicknessOptions: { label: string; value: EdgeLineThickness }[] = [
        { label: t('labelEdgeThin'), value: 'thin' },
        { label: t('labelEdgeMedium'), value: 'medium' },
        { label: t('labelEdgeThick'), value: 'thick' }
    ];

    return (
        <div className="flex flex-col gap-2 px-3 py-2 min-w-[200px] h-[370px] overflow-auto">
            {/* Graphic Settings */}
            <span className="text-xs font-medium text-text-light uppercase tracking-wide">
                {t('labelGraphicsSettings')}
            </span>

            <Switch
                checked={graphicsSettings.shadowsEnabled}
                label={t('labelShadows')}
                size="xs"
                onChange={(e) => setGraphicsSettings({ shadowsEnabled: e.currentTarget.checked })}
            />

            <Switch
                checked={graphicsSettings.reflectionsEnabled}
                label={t('labelReflections')}
                size="xs"
                onChange={(e) => setGraphicsSettings({ reflectionsEnabled: e.currentTarget.checked })}
            />

            {/* Ambient Occlusion */}
            <span className="text-xs font-medium text-text-light uppercase">{t('labelAmbientOcclusion')}</span>
            <Button.Group>
                {aoOptions.map((opt) => (
                    <Button
                        key={opt.value}
                        className="flex-1 data-[selected=true]:bg-background-action-hover data-[selected=false]:bg-background-default justify-center"
                        data-selected={graphicsSettings.aoQuality === opt.value}
                        size="xs"
                        tabIndex={-1}
                        variant={variants.button.secondary}
                        onClick={() => setGraphicsSettings({ aoQuality: opt.value })}
                    >
                        {opt.label}
                    </Button>
                ))}
            </Button.Group>

            <Divider />

            {/* Multisampling */}
            <span className="text-xs font-medium text-text-light uppercase">{t('labelMultisampling')}</span>
            <Button.Group>
                {multisamplingOptions.map((opt) => (
                    <Button
                        key={opt.value}
                        className="flex-1 data-[selected=true]:bg-background-action-hover data-[selected=false]:bg-background-default justify-center"
                        data-selected={graphicsSettings.multisampling === opt.value}
                        size="xs"
                        tabIndex={-1}
                        variant={variants.button.secondary}
                        onClick={() => setGraphicsSettings({ multisampling: opt.value })}
                    >
                        {opt.label}
                    </Button>
                ))}
            </Button.Group>

            <Divider />

            {/* Article Edges */}
            <span className="text-xs font-medium text-text-light uppercase">{t('labelArticleEdges')}</span>
            <Tooltip
                disabled={!aoIsActive}
                label={t('labelEdgesDisabledByAo')}
                position="left"
                variant={variants.tooltip.left}
            >
                <div>
                    <Switch
                        checked={graphicsSettings.edgesOverlayEnabled}
                        className="mb-3"
                        disabled={aoIsActive}
                        label={t('labelEdgesOverlay')}
                        size="xs"
                        onChange={(e) => setGraphicsSettings({ edgesOverlayEnabled: e.currentTarget.checked })}
                    />

                    <Button.Group>
                        {edgeThicknessOptions.map((opt) => (
                            <Button
                                key={opt.value}
                                className="flex-1 data-[selected=true]:bg-background-action-hover data-[selected=false]:bg-background-default justify-center"
                                data-selected={graphicsSettings.edgeLineThickness === opt.value}
                                disabled={aoIsActive || !graphicsSettings.edgesOverlayEnabled}
                                size="xs"
                                tabIndex={-1}
                                variant={variants.button.secondary}
                                onClick={() => setGraphicsSettings({ edgeLineThickness: opt.value })}
                            >
                                {opt.label}
                            </Button>
                        ))}
                    </Button.Group>
                </div>
            </Tooltip>

            <Divider />

            {/* Material Quality */}
            <span className="text-xs font-medium text-text-light uppercase">{t('labelMaterialQuality')}</span>
            <Button.Group>
                {materialOptions.map((opt) => (
                    <Button
                        key={opt.value}
                        className="flex-1 data-[selected=true]:bg-background-action-hover data-[selected=false]:bg-background-default justify-center"
                        data-selected={graphicsSettings.materialQuality === opt.value}
                        size="xs"
                        tabIndex={-1}
                        variant={variants.button.secondary}
                        onClick={() => setGraphicsSettings({ materialQuality: opt.value })}
                    >
                        {opt.label}
                    </Button>
                ))}
            </Button.Group>

            <Divider />

            {/* Developer Settings */}
            <span className="text-xs font-medium text-text-light uppercase tracking-wide">
                {t('labelDeveloperSettings')}
            </span>

            <Switch
                checked={graphicsSettings.showIfc}
                label={t('labelShowIfc')}
                size="xs"
                onChange={(e) => setGraphicsSettings({ showIfc: e.currentTarget.checked })}
            />

            <Switch
                checked={graphicsSettings.showFps}
                label={t('labelShowFps')}
                size="xs"
                onChange={(e) => setGraphicsSettings({ showFps: e.currentTarget.checked })}
            />

            {/* Content Developer */}
            {IS_DEV_TOOLS && (
                <>
                    <Divider />
                    <span className="text-xs font-medium text-text-light uppercase tracking-wide">
                        {t('labelContentDeveloper')}
                    </span>
                    <Switch
                        checked={prefabLayerTreeOpen}
                        label={t('labelPrefabLayerTree')}
                        size="xs"
                        onChange={(e) => setPrefabLayerTreeOpen(e.currentTarget.checked)}
                    />
                </>
            )}

            {/* Camera Frustum Viewer */}
            {IS_DEV_ENV && (
                <>
                    <Divider />
                    <span className="text-xs font-medium text-text-light uppercase tracking-wide">
                        {t('labelCameraFrustumViewer')}
                    </span>
                    <Switch
                        checked={frustumViewerOpen}
                        label={t('labelShowCameraFrustumViewer')}
                        size="xs"
                        onChange={(e) => handleFrustumViewerToggle(e.currentTarget.checked)}
                    />
                </>
            )}
        </div>
    );
};
